import { SEGMENT_NAME_MAX, type MatchType, type SegmentRule } from "@domains/segments/shared/segmentsTypes.ts";
import { Button } from "@/components/ui/button.tsx";
import { FieldError, Label } from "@/components/ui/field.tsx";
import { Input } from "@/components/ui/input.tsx";
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "@/components/ui/modal.tsx";
import { TextField } from "@/components/ui/text-field.tsx";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useCreateSegmentMutationOptions } from "@domains/segments/client/api/segmentsQuery.ts";
import { validateSegmentName } from "@domains/segments/client/components/validateSegmentName.ts";
import { SegmentTags } from "@domains/segments/client/components/tags/SegmentTags.tsx";
import { useI18nextInstance } from "@squide/i18next";
import { useForm } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { i18NextInstanceKey } from "../i18n/i18next.ts";

export interface SaveAsSegmentDialogProps {
    matchType: MatchType;
    rules: SegmentRule[];
    onBack: () => void;
    onSaved: (segmentId: string) => void;
    onIsSavingChange: (isSaving: boolean) => void;
}

export function SaveAsSegmentDialog({ matchType, rules, onBack, onSaved, onIsSavingChange }: SaveAsSegmentDialogProps) {
    const i18nextInstance = useI18nextInstance(i18NextInstanceKey);
    const { t } = useTranslation("SaveAsSegmentDialog", { i18n: i18nextInstance });

    const createMutation = useMutation(useCreateSegmentMutationOptions());

    useEffect(() => {
        onIsSavingChange(createMutation.isPending);

        return () => onIsSavingChange(false);
    }, [createMutation.isPending, onIsSavingChange]);

    const validateName = useCallback((value: string) => validateSegmentName(value, { required: t("name.required"), tooLong: t("name.tooLong", { count: SEGMENT_NAME_MAX }) }), [t]);

    const form = useForm({
        defaultValues: { name: "", tags: [] as string[] },
        onSubmit: async ({ value }) => {
            try {
                const { id } = await createMutation.mutateAsync({ name: value.name.trim(), isRuleBased: true, matchType, rules, tags: value.tags });
                toast.success(t("createdToast"), { position: "top-right" });
                onSaved(id);
            } catch {
                toast.error(t("saveErrorToast"), { position: "top-right" });
            }
        }
    });

    const handleTagsChange = useCallback((tags: string[]) => form.setFieldValue("tags", tags), [form]);

    const handleSubmit = useCallback(
        (event: FormEvent) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
        },
        [form]
    );

    return (
        <form noValidate onSubmit={handleSubmit} className="contents">
            <ModalHeader className="flex flex-row items-center gap-2 space-y-0">
                <Button type="button" intent="plain" size="sq-sm" isDisabled={createMutation.isPending} onPress={onBack} aria-label={t("back")}>
                    <ArrowLeftIcon aria-hidden="true" />
                </Button>
                <ModalTitle>{t("title")}</ModalTitle>
            </ModalHeader>
            <ModalBody className="flex flex-col gap-4">
                <form.Field name="name" validators={{ onChange: ({ value }) => validateName(value), onSubmit: ({ value }) => validateName(value) }}>
                    {field => (
                        <TextField value={field.state.value} onChange={field.handleChange} isInvalid={field.state.meta.errors.length > 0} isDisabled={createMutation.isPending} autoFocus>
                            <Label>{t("name.label")}</Label>
                            <Input name="name" onBlur={field.handleBlur} placeholder={t("name.placeholder")} />
                            <FieldError>{field.state.meta.errors[0]}</FieldError>
                        </TextField>
                    )}
                </form.Field>

                <form.Field name="tags">{field => <SegmentTags tags={field.state.value} onChange={handleTagsChange} />}</form.Field>
            </ModalBody>
            <ModalFooter>
                <Button type="button" intent="plain" onPress={onBack} isDisabled={createMutation.isPending}>
                    {t("cancel")}
                </Button>
                <Button type="submit" intent="primary" isPending={createMutation.isPending} name="save-segment">
                    {t("save")}
                </Button>
            </ModalFooter>
        </form>
    );
}
